import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Textarea, Card, Tooltip, Modal } from '../../components';
import {
  FinalPitchForReview,
  getFinalPitchForReview,
  getFinalPitchDeckUrl,
  gradeFinalPitch,
} from '../../services/pitch-submission-service';

const ReadOnlyField: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <label className="block text-sm font-medium text-muted mb-1">{label}</label>
      <div className="bg-shell rounded-lg p-3 text-text text-sm leading-relaxed whitespace-pre-wrap">{value}</div>
    </div>
  );
};

type Outcome = {
  label: string;
  reviewStatus: 'rejected' | 'revision_needed' | 'approved' | 'approved_distinction';
  distinction: boolean;
  tone: 'danger' | 'warning' | 'success' | 'chart';
  requiresFeedback: boolean;
  message: string;
};

const deriveOutcome = (raw: string): Outcome | null => {
  if (raw === '') return null;
  const n = Number(raw);
  if (Number.isNaN(n) || n < 0 || n > 100) return null;
  if (n < 40)  return { label: 'Rejected',                 reviewStatus: 'rejected',             distinction: false, tone: 'danger',  requiresFeedback: true,  message: 'Submission will be rejected. Feedback is required.' };
  if (n < 60)  return { label: 'Needs Revision',            reviewStatus: 'revision_needed',      distinction: false, tone: 'warning', requiresFeedback: true,  message: 'Founder will be asked to revise and resubmit. Feedback on what to revise is required.' };
  if (n < 80)  return { label: 'Approved',                  reviewStatus: 'approved',             distinction: false, tone: 'success', requiresFeedback: false, message: 'Submission will be approved.' };
  return              { label: 'Approved with Distinction', reviewStatus: 'approved_distinction', distinction: true,  tone: 'chart',   requiresFeedback: false, message: 'Submission will be approved with distinction — exceptional work.' };
};

const toneClasses: Record<Outcome['tone'], { pill: string; bar: string }> = {
  danger:  { pill: 'bg-danger/15 text-danger border-danger/30',    bar: 'bg-danger' },
  warning: { pill: 'bg-warning/15 text-warning border-warning/30', bar: 'bg-warning' },
  success: { pill: 'bg-success/15 text-success border-success/30', bar: 'bg-success' },
  chart:   { pill: 'bg-chart/15 text-chart border-chart/30',       bar: 'bg-chart' },
};

const FinalPitchReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [pitch, setPitch]           = useState<FinalPitchForReview | null>(null);
  const [loading, setLoading]       = useState(true);
  const [marks, setMarks]           = useState('');
  const [feedback, setFeedback]     = useState('');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [processing, setProcessing]     = useState(false);
  const [deckLoading, setDeckLoading]   = useState(false);

  useEffect(() => {
    getFinalPitchForReview(id!)
      .then(p => setPitch(p))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading || !pitch) {
    return (
      <div className="section-gap">
        <p className="text-muted">{loading ? 'Loading…' : 'Submission not found.'}</p>
      </div>
    );
  }

  const rs = pitch.reviewStatus;

  // Graded but founder hasn't resubmitted yet
  const awaitingResubmission = rs === 'revision_needed' || rs === 'rejected';
  // Fully approved — no more grading needed
  const isApproved = rs === 'approved' || rs === 'approved_distinction';
  // Can still grade
  const canGrade = rs === 'pending';

  const outcome    = deriveOutcome(marks);
  const marksNum   = marks === '' ? NaN : Number(marks);
  const marksInvalid    = marks !== '' && (Number.isNaN(marksNum) || marksNum < 0 || marksNum > 100);
  const feedbackRequired = !!outcome?.requiresFeedback;
  const canSubmit  = !!outcome && (!feedbackRequired || feedback.trim().length > 0);

  const handleSubmit = async () => {
    if (!outcome || !canSubmit) return;
    setProcessing(true);
    try {
      await gradeFinalPitch(pitch._id!, { score: marksNum, mentorFeedback: feedback.trim() });
      navigate('/app/dashboard/mentor');
    } catch (e) { console.error(e); }
    finally { setProcessing(false); }
  };

  const handleOpenDeck = async () => {
    setDeckLoading(true);
    try {
      const { url } = await getFinalPitchDeckUrl(id!);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch { /* deck may not exist */ }
    finally { setDeckLoading(false); }
  };

  const founderName   = pitch.userId?.name || 'Unknown Founder';
  const submittedDate = pitch.submittedAt
    ? new Date(pitch.submittedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '—';

  // Status banner shown in the scoring column for already-graded pitches
  const GradedBanner = () => {
    if (!rs || rs === 'pending') return null;
    const toneMap = {
      revision_needed:      { bg: 'bg-warning/10 border-warning/20 text-warning', label: 'Needs Revision' },
      rejected:             { bg: 'bg-danger/10 border-danger/20 text-danger',    label: 'Rejected' },
      approved:             { bg: 'bg-success/10 border-success/20 text-success', label: 'Approved' },
      approved_distinction: { bg: 'bg-chart/10 border-chart/20 text-chart',       label: 'Approved ★ Distinction' },
    } as const;
    const t = toneMap[rs as keyof typeof toneMap];
    if (!t) return null;
    return (
      <div className={`rounded-card border p-4 ${t.bg}`}>
        <p className="text-xs font-semibold uppercase tracking-wider opacity-75 mb-1">Previous Grade</p>
        <div className="flex items-baseline gap-3">
          <span className="font-display text-2xl tabular-nums">{pitch.score}/100</span>
          <span className="font-semibold text-sm">{t.label}</span>
        </div>
        {pitch.mentorFeedback && (
          <p className="text-xs mt-2 leading-relaxed opacity-90 whitespace-pre-wrap">{pitch.mentorFeedback}</p>
        )}
        {pitch.gradedAt && (
          <p className="text-xs mt-1.5 opacity-60">
            Graded {new Date(pitch.gradedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {pitch.gradedBy && ` · ${(pitch.gradedBy as any).name}`}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Tooltip content="Return to dashboard" position="right">
          <button onClick={() => navigate('/app/dashboard/mentor')} className="p-2 rounded-lg hover:bg-shell transition-colors">
            <svg className="w-5 h-5 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </Tooltip>
        <div>
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-xs font-medium bg-chart/15 text-chart px-2 py-0.5 rounded-full">Final Pitch</span>
            {(pitch.submissionCount ?? 0) > 1 && (
              <span className="text-xs text-muted">Submission #{pitch.submissionCount}</span>
            )}
            {isApproved && (
              <span className="text-xs font-medium bg-success/15 text-success px-2 py-0.5 rounded-full">
                {rs === 'approved_distinction' ? 'Approved ★ Distinction' : 'Approved'}
              </span>
            )}
            {awaitingResubmission && (
              <span className="text-xs font-medium bg-warning/15 text-warning px-2 py-0.5 rounded-full">
                Awaiting Resubmission
              </span>
            )}
          </div>
          <h1 className="text-2xl font-semibold text-text">{founderName}</h1>
          <p className="text-muted mt-0.5 text-sm">Submitted {submittedDate}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left — pitch content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h2 className="text-lg font-semibold text-text mb-5">The Opportunity</h2>
            <div className="space-y-4">
              <ReadOnlyField label="Problem"           value={pitch.problem} />
              <ReadOnlyField label="Solution"          value={pitch.solution} />
              <ReadOnlyField label="Target Market"     value={pitch.targetMarket} />
              <ReadOnlyField label="Market Size"       value={pitch.marketSize} />
              <ReadOnlyField label="Industry"          value={pitch.industry} />
              <ReadOnlyField label="Geographic Focus"  value={pitch.geographicFocus} />
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-text mb-5">The Business</h2>
            <div className="space-y-4">
              <ReadOnlyField label="Stage"         value={pitch.stage} />
              <ReadOnlyField label="Revenue Model" value={pitch.revenueModel} />
              <ReadOnlyField label="Traction"      value={pitch.traction} />
              <ReadOnlyField label="Team Size"     value={pitch.teamSize} />
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-text mb-5">The Ask</h2>
            <div className="space-y-4">
              <ReadOnlyField label="Funding Stage"  value={pitch.fundingStage} />
              <ReadOnlyField label="Funding Ask"    value={pitch.fundingAsk} />
              <ReadOnlyField label="Equity Offered" value={pitch.equityOffered} />
              <ReadOnlyField label="Use of Funds"   value={pitch.useOfFunds} />
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-text mb-5">Competitive Edge</h2>
            <div className="space-y-4">
              <ReadOnlyField label="Competitors"     value={pitch.competitors} />
              <ReadOnlyField label="Differentiation" value={pitch.differentiation} />
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-text mb-5">Roadmap</h2>
            <ReadOnlyField label="12-Month Milestones" value={pitch.milestones} />
          </Card>

          {pitch.slideDeckName && (
            <Card>
              <h2 className="text-lg font-semibold text-text mb-3">Slide Deck</h2>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text truncate">{pitch.slideDeckName}</p>
                  {(pitch.slideDeckSize ?? 0) > 0 && (
                    <p className="text-xs text-muted mt-0.5">
                      {((pitch.slideDeckSize ?? 0) / (1024 * 1024)).toFixed(1)} MB
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenDeck}
                  disabled={deckLoading}
                >
                  {deckLoading ? (
                    'Opening…'
                  ) : (
                    <span className="flex items-center gap-1.5">
                      View
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </span>
                  )}
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Right — scoring */}
        <aside className="lg:col-span-1 space-y-6 lg:sticky lg:top-6 lg:self-start">

          {/* Already graded — show summary, hide scoring UI */}
          {(awaitingResubmission || isApproved) && <GradedBanner />}

          {/* Approved — nothing more to do */}
          {isApproved && (
            <Card>
              <p className="text-sm text-muted text-center py-4">
                This pitch has been approved. No further action required.
              </p>
            </Card>
          )}

          {/* Awaiting resubmission — scoring cards disabled */}
          {awaitingResubmission && (
            <div className="relative">
              <div className="absolute inset-0 z-10 rounded-card bg-bg/60 backdrop-blur-[1px] flex items-center justify-center">
                <p className="text-sm text-muted text-center px-6">
                  Waiting for founder to resubmit before you can grade again.
                </p>
              </div>
              <div className="opacity-40 pointer-events-none space-y-6">
                <Card>
                  <h2 className="text-lg font-semibold text-text mb-4">Score this pitch</h2>
                  <div className="h-12 bg-surface rounded-card" />
                  <div className="mt-4 h-16 bg-surface rounded-card" />
                </Card>
              </div>
            </div>
          )}

          {/* Ready to grade */}
          {canGrade && (
            <>
              <Card>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <h2 className="text-lg font-semibold text-text">Score this pitch</h2>
                  <Tooltip content="Review your score, then submit" position="left">
                    <Button
                      onClick={() => setConfirmOpen(true)}
                      disabled={!canSubmit || processing || marksInvalid}
                      size="sm"
                    >
                      Submit
                    </Button>
                  </Tooltip>
                </div>
                <p className="text-muted text-xs mb-4">Outcome is derived automatically from the score.</p>

                <div className="mb-4">
                  <label htmlFor="marks" className="block text-xs font-medium text-text mb-1.5">
                    Score <span className="text-muted">(0 – 100)</span>
                  </label>
                  <div className="relative">
                    <input
                      id="marks"
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={marks}
                      onChange={e => setMarks(e.target.value)}
                      placeholder="—"
                      className={`w-full pl-4 pr-12 py-3 rounded-card border bg-shell text-text placeholder:text-muted/50 focus:outline-none focus:ring-2 focus:ring-chart/30 focus:border-chart transition-all text-2xl font-display tabular-nums ${
                        marksInvalid ? 'border-danger' : 'border-line'
                      }`}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted text-sm">/ 100</span>
                  </div>
                  {marksInvalid && <p className="text-danger text-xs mt-1">Score must be between 0 and 100.</p>}
                </div>

                <div className={`rounded-card border p-4 ${outcome ? toneClasses[outcome.tone].pill : 'bg-surface text-muted border-line'}`}>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-semibold opacity-75">Outcome</span>
                    {outcome?.distinction && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-chart text-black tracking-wide uppercase">★ Distinction</span>
                    )}
                  </div>
                  <div className="font-display text-xl tracking-wide leading-tight">
                    {outcome ? outcome.label : 'Enter a score'}
                  </div>
                  <p className="text-xs leading-relaxed opacity-90 mt-1.5">
                    {outcome ? outcome.message : 'Enter a score from 0 to 100 to see the automatic outcome.'}
                  </p>
                  {outcome && (
                    <div className="mt-3 h-1.5 w-full bg-shell/50 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${toneClasses[outcome.tone].bar} transition-all duration-300`}
                        style={{ width: `${Math.max(0, Math.min(100, marksNum))}%` }}
                      />
                    </div>
                  )}
                </div>

                <div className="mt-4 bg-surface border border-line rounded-card p-4">
                  <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-2">Grading Rubric</div>
                  <ul className="space-y-1.5 text-xs">
                    <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-danger mt-1.5 flex-shrink-0" /><span className="text-text"><span className="font-semibold">&lt; 40</span> <span className="text-muted">— Rejected</span></span></li>
                    <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-warning mt-1.5 flex-shrink-0" /><span className="text-text"><span className="font-semibold">40 – 59</span> <span className="text-muted">— Needs revision (with feedback)</span></span></li>
                    <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-success mt-1.5 flex-shrink-0" /><span className="text-text"><span className="font-semibold">60 – 79</span> <span className="text-muted">— Approved</span></span></li>
                    <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-chart mt-1.5 flex-shrink-0" /><span className="text-text"><span className="font-semibold">80 – 100</span> <span className="text-muted">— Approved with distinction</span></span></li>
                  </ul>
                </div>
              </Card>

              <Card>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(v => !v)}
                  aria-expanded={feedbackOpen}
                  className="w-full flex items-center justify-between gap-3 text-left focus:outline-none focus:ring-2 focus:ring-chart/30 rounded-md"
                >
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h2 className="text-lg font-semibold text-text">Feedback</h2>
                    <span className={`text-[10px] italic px-2 py-0.5 rounded-full border ${
                      feedbackRequired
                        ? 'bg-warning/15 text-warning border-warning/30 not-italic font-medium'
                        : 'text-muted border-line'
                    }`}>
                      {feedbackRequired ? 'Required' : 'Optional'}
                    </span>
                    {feedback.trim() && (
                      <span className="text-[10px] text-success italic">· {feedback.trim().length} chars</span>
                    )}
                  </div>
                  <svg className={`w-4 h-4 text-muted flex-shrink-0 transition-transform ${feedbackOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {feedbackOpen && (
                  <div className="mt-4 animate-fade-in">
                    <Textarea
                      name="feedback"
                      placeholder={
                        outcome?.reviewStatus === 'rejected'       ? 'Explain why this pitch was rejected…'
                        : outcome?.reviewStatus === 'revision_needed' ? 'Explain specifically what needs to be revised…'
                        : 'Share notes or encouragement for the founder…'
                      }
                      value={feedback}
                      onChange={e => setFeedback(e.target.value)}
                      rows={6}
                    />
                  </div>
                )}
              </Card>
            </>
          )}
        </aside>
      </div>

      {/* Confirmation modal */}
      <Modal isOpen={confirmOpen} onClose={() => !processing && setConfirmOpen(false)} title="Confirm grade">
        <div className="space-y-5">
          <p className="text-text text-sm leading-relaxed">
            You've scored this final pitch <span className="font-semibold tabular-nums">{marksNum}/100</span>.
            {outcome && (
              <> The outcome is{' '}
                <span className={`font-semibold ${
                  outcome.tone === 'danger' ? 'text-danger' : outcome.tone === 'warning' ? 'text-warning'
                  : outcome.tone === 'success' ? 'text-success' : 'text-chart'
                }`}>{outcome.label}</span>.
              </>
            )}
          </p>
          {feedbackRequired && !feedback.trim() && (
            <p className="text-xs italic text-warning">Feedback is required for this outcome — go back and add it before confirming.</p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button type="button" onClick={() => setConfirmOpen(false)} disabled={processing}
              className="w-full sm:w-auto px-5 py-2.5 rounded-card border border-line bg-shell text-text text-sm font-medium hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              Cancel
            </button>
            <button type="button" onClick={async () => { await handleSubmit(); setConfirmOpen(false); }} disabled={processing || !canSubmit}
              className="w-full sm:w-auto px-5 py-2.5 rounded-card bg-chart text-black text-sm font-semibold hover:bg-chart/90 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed">
              {processing ? 'Submitting…' : 'Confirm Grade'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FinalPitchReview;
